/**
 * CHART VIEWPORT — the time-based x-axis and its interactions (chart P1, HUDCHAT-p).
 *
 * ⛔⛔ THE DEFECT THIS MODULE CURES (CHART-GAP, measured on production 2026-08-12 14:0xZ,
 * named in seriesGap.ts): ChartCanvas placed bars BY ARRAY INDEX, so 152 absent minutes
 * occupied ZERO pixels and a halt VANISHED — bars either side drawn touching. Every
 * mapping in this module is TIME-LINEAR: x is a function of the bar's own timestamp
 * against the viewport window, so a missing minute occupies exactly one minute of
 * x-space whether or not a bar exists there. The gap cure is the GEOMETRY LEG —
 * seriesGap.ts stays as the detection leg and must still not be cited as a cure.
 *
 * PURE MATH ONLY. No DOM, no state framework — every function takes a viewport and
 * returns a value or a NEW viewport, so `node --test` can drive mutation arms against
 * the exact transforms the panel wires to wheel/drag events.
 *
 * FOLLOW MODE is the current behaviour made explicit: the window pinned to the newest
 * bar, auto-advancing on every poll. It is DERIVED, never sticky-through-interaction:
 * `panBy`/`zoomAt` recompute it from where the right edge LANDS relative to the live
 * edge, so panning into history drops follow and panning back to the edge restores it —
 * there is no hidden boolean that can disagree with what the screen shows.
 */

export interface Viewport {
  /** Left edge of the visible window, epoch ms. */
  fromTs: number;
  /** Right edge of the visible window, epoch ms. Always > fromTs. */
  toTs: number;
  /** Live-follow: pinned to the newest bar, auto-advancing on each poll. */
  follow: boolean;
}

// Board ① consumes the calendar from session.ts — it never re-derives one.
export type Tf = "1" | "3" | "6" | "12" | "24" | "48" | "180" | "360" | "1440";
/**
 * ⛔⛔ THIS IS A SECOND LADDER, AND IT MUST BE KEPT IN STEP WITH THE ROUTE'S BY HAND.
 * The worker owns `CANDLE_TF_LADDER` (workers/read-api/src/candlesRange.ts) and REFUSES any rung
 * not on it; this one decides which pills the owner can press. They are two packages, so neither
 * can import the other and the coupling cannot be derived away.
 * ⇒ ***A DUPLICATE CONSTANT ACROSS A PACKAGE BOUNDARY IS A DIVERGENCE WITH A DELAY ON IT*** —
 * `cvd.ts` refuses exactly this shape inside one package, and here it is unavoidable.
 * MEASURED, at my own hand, R772②: I added 48m and 360m to the ROUTE and the pills still offered
 * five rungs. The route served two timeframes no button could ever ask for — the day's
 * built-and-not-connected class, created by me, in the same hour I filed it against others.
 * ⚠ ADD A RUNG HERE AND ON THE ROUTE, OR IT IS DEAD IN ONE DIRECTION: a pill with no route
 * refuses at the API; a route with no pill is unreachable.
 * ⭐ Nothing else needs editing: `tfSpanMs` DERIVES the span from the rung's own value.
 */
export const TF_LADDER: Tf[] = ["1", "3", "6", "12", "24", "48", "180", "360", "1440"];

/**
 * The span a bar at rung `tf` COVERS, in ms — the ONE shared derivation (DESK-n25 R497(2)).
 *
 * ⛔ WHY THIS EXISTS AS A FUNCTION AND NOT AS A CONSTANT AT EACH CALL SITE. A bar's `t` is the
 * START of its bucket, so at every rung above 1m the newest bar's timestamp sits up to one span
 * BEFORE the newest data. `feedState` compared that start against the close and asked whether the
 * feed had stopped early; at 12m/180m/1440m the answer was structurally yes, on a perfectly
 * healthy feed, at every close. Measured on the live route 2026-08-22:
 * ```
 *   tf=1   last t = 20:59:00Z      tf=12  last t = 20:48:00Z      same real end, 11m apart
 * ```
 * ⇒ ***THREE OF FIVE SHIPPED RUNGS COULD NEVER REPORT `CLOSED`*** (GK-vv drove the boundary to
 * exactly `CLOSE_CONSISTENCY_GRACE_S`). The cure is to give the consumer the bucket's WIDTH so it
 * can reason about the bucket's END — never to widen the grace, which would let `CLOSED` excuse a
 * day-long outage on the 1m rung.
 *
 * ⭐ IT IS DERIVED FROM THE RUNG'S OWN VALUE, not a hand-written map: the ladder is minutes, so a
 * rung added to `TF_LADDER` is spanned correctly here with no edit. A second table would be the
 * two-copies class `sessionSegments` refuses to commit for the calendar.
 */
export function tfSpanMs(tf: Tf): number {
  return Number(tf) * 60_000;
}

/**
 * The span to GRADE a bucket by, taken from the rung the ROUTE ACTUALLY APPLIED.
 *
 * ⛔ AN UNREPORTED OR OFF-LADDER RUNG YIELDS `0`, WHICH IS A REFUSAL TO WIDEN, NOT A GUESS.
 * `api.ts` already rules this for `tf_applied`: *"absent means NO MISMATCH CLAIM ... never default
 * it to the requested tf: that would make the lane certify agreement it never measured."* A span
 * is stronger than a label — it is used to EXCUSE SILENCE — so guessing one would let `CLOSED`
 * explain a gap on a rung nobody measured. `0` can only make the verdict more conservative
 * (`STALE`), never falsely quiet, which is the direction `feedState`'s own docblock demands:
 * *a false STALE costs a glance, a false CLOSED hides a dead feed.*
 *
 * ⚠ USE THIS, NOT `tfSpanMs(resolveTf(...).shown)`, ANYWHERE THE VERDICT IS OWNER-VISIBLE:
 * `resolveTf` deliberately falls back to the REQUESTED rung so a pane can still draw and flag a
 * mismatch. That is right for drawing and wrong for excusing.
 */
export function servedSpanMs(tfApplied: string | null | undefined): number {
  const rung = TF_LADDER.find((candidate) => candidate === tfApplied);
  return rung === undefined ? 0 : tfSpanMs(rung);
}

/** What a pane displays: a valid served timeframe always outranks its request. */
export function resolveTf(
  requested: Tf,
  servedRaw: string | null | undefined,
): { shown: Tf; mismatch: boolean } {
  const served = TF_LADDER.find((candidate) => candidate === servedRaw);
  return served === undefined
    ? { shown: requested, mismatch: true }
    : { shown: served, mismatch: served !== requested };
}

/** Aggregated buckets have three honest states; partial data is neither full nor absent. */
/**
 * ⛔ FOUR STATES, AND THE FOURTH IS THE ONE THAT EMPTIED A PANE (GK-ff `c8c54629` ④).
 * `unknown` ≠ `gap`. A worker that does not report `src_bars` has told us NOTHING about the
 * bucket — it has not told us the bucket is empty. The panel FILTERS gaps, so scoring absence
 * as `gap` deleted real bars: driven at the gate, tf=12 kept 0 of 3.
 * ⇒ ***UNREPORTED IS NOT ZERO. A BAR WITH UNKNOWN PROVENANCE IS STILL A BAR.***
 * ⚠ `expected` may also be unknown: vv measured tf=1440 → 10 of 10 "partial" against a NOMINAL
 * 1440-minute expectation. A trading day never holds 1440 spine minutes, so a nominal
 * expectation manufactures partiality. **No expectation ⇒ no partiality claim.**
 */
export function bucketState(
  srcBars: number | null | undefined,
  expected: number | null | undefined,
): "complete" | "partial" | "gap" | "unknown" {
  if (srcBars === 0) return "gap";
  if (srcBars == null) return "unknown";
  // ⛔ GK-ff ⑤ (`dfb47ee2`): MY OWN CURE WEAKENED THIS GUARD. At @aab75bb `srcBars <= 0` scored
  // `gap` and the filter dropped it; after the `unknown` cure, `-5` fell through to the
  // `>= expected` comparison and drew as a LEGITIMATE PARTIAL BUCKET. P5 loops only non-negative
  // values, so no arm saw it. ⇒ ***A NEGATIVE COUNT IS CORRUPT PROVENANCE, NOT A SMALL ONE.***
  // It scores `unknown`: the bar is kept (dropping real bars is the defect I just cured) but no
  // partiality is claimed about it.
  if (srcBars < 0) return "unknown";
  if (expected == null || !(expected > 0)) return "unknown";
  return srcBars >= expected ? "complete" : "partial";
}

/**
 * ⛔ EXTRACTED SO THE BANNER IS A DRIVEN VALUE (GK-ff ⑤). Two mutants survived my source-text
 * arms: `{#if false && …}` (the banner can never render) and replacing the real call with a
 * COMMENT naming it. ⇒ ***A GREP MEASURES THAT TEXT EXISTS, NOT THAT IT RENDERS — it cannot
 * tell a call from a comment.*** The template must call this and nothing else.
 */
export function shouldWarnTfMismatch(hasFeed: boolean, mismatch: boolean): boolean {
  return hasFeed && mismatch;
}

/** The cadence floor — the P0 ask, and the smallest payload any pane requests. */
export const CANDLE_CADENCE_LIMIT = 240;

/** Parity with the read-api's `MAX_LIMIT_CANDLES` (candlesChartV3): asking past it is a lie. */
export const CANDLE_REACH_LIMIT = 4320;

/**
 * THE FIRST WINDOW'S WIDTH IN BARS, PER SERVED RUNG — R300's receipt, not decoration.
 *
 * ⛔ WHY A TABLE AND NOT THE `limit` PROP. `limit` is 240 at every rung and it sizes the first
 * window (`span = limit * BAR_MS`). At tf=1440 that is 240 DAYS — about eight months — so a
 * daily chart opened on the whole 627-bar archive still showed the owner back to roughly
 * December, and R286's receipt ("a 2024 bar ON SCREEN") could not be met no matter how deep
 * the ASK went. **Fixing the ask alone leaves the window narrow; fixing the window alone leaves
 * the payload short. Both, or neither.**
 *
 * 730 bars = 24 months, chosen to match the capability the desk and both gate seats measured
 * and published (627 bars available, oldest 2024-08-18). It is deliberately a little WIDER than
 * the archive: `clampToData` bounds the window to real data, so an over-wide default settles
 * onto the true edge instead of inventing empty span.
 *
 * ⚠ A DESIGN DECISION, recorded as one: the ratified mock does not specify a daily default
 * window. Rungs absent from this table keep the historical `limit`-sized behaviour.
 */
export const DEFAULT_WINDOW_BARS: Readonly<Record<string, number>> = Object.freeze({ "1440": 730 });

/** The first window's bar count for a served rung — the `limit` prop where unspecified. */
export function defaultWindowBars(shownRung: string, limitBars: number): number {
  return DEFAULT_WINDOW_BARS[shownRung] ?? limitBars;
}

/**
 * THE CANDLE ASK, DERIVED FROM THE DRAWN WINDOW — **IN BARS**.
 *
 * ⛔ WHY THIS IS NOT `cvdAskLimit`, WHICH SOLVES THE SAME PROBLEM 500 LINES AWAY (R300, desk
 * 2026-08-20). The CVD lane got this cure first and its SHAPE is right — *"the window IS the
 * measurement the flag was standing in for; ask for what is drawn"* (desk GO `06177009`). But
 * `cvdAskLimit` measures the window in MINUTES (`windowMs / 60_000`), and that is rung-blind.
 * At tf=1440 a two-year window is 1,051,200 "minutes", which clamps to the ceiling: it returns
 * a usable number **by clamp accident** while asking 4320 for ~730 bars.
 * ⇒ ***A CANDLE ASK IS A BAR COUNT.*** Dividing a span by `60_000` instead of by `BAR_MS` is the
 * same factor error GK-pp named in §21.9 — one rung up it is wrong by `BAR_MS / 60_000`.
 * **Reuse the principle, never the minutes-shaped function.**
 *
 * ⚠ `barMs` is a DIVISOR and must be positive: zero yields `Infinity` bars — a silent request
 * for the whole archive, which is exactly the shape of failure law 1 exists to forbid. Both
 * arguments fail LOUD rather than coercing to a bound that would look deliberate.
 */
export function candleAskLimit(windowMs: number, barMs: number): number {
  if (!Number.isFinite(windowMs) || windowMs < 0) {
    throw new Error(
      `candleAskLimit: window must be a non-negative finite ms span; got ${windowMs}`,
    );
  }
  if (!Number.isFinite(barMs) || barMs <= 0) {
    throw new Error(`candleAskLimit: barMs must be a positive finite ms span; got ${barMs}`);
  }
  const bars = Math.ceil(windowMs / barMs);
  return Math.min(CANDLE_REACH_LIMIT, Math.max(CANDLE_CADENCE_LIMIT, bars));
}

/**
 * The CVD LANE's own rung claim (R55a) — extracted for the same reason as its sibling above:
 * a banner nobody can drive is a banner nobody can arm.
 *
 * ⚠ IT IS NOT `resolveTf(...).mismatch` AND MUST NOT BECOME IT. `resolveTf` reports a mismatch
 * for an unrecognised served value, which is correct for candles (`feed.tf` always exists) and
 * WRONG for the lane, whose `tf_applied` is OPTIONAL. Absence means the worker told us nothing
 * about its rung — not that it disagreed.
 * ⇒ ***AN UNREPORTED RUNG IS NOT A WRONG RUNG*** — the `bucketState` law (`unknown` ≠ `gap`)
 * one field over. Claiming a mismatch we never measured is manufactured partiality, which is
 * the defect that hatched every bucket on a daily pane.
 */
export function cvdRungMismatch(
  requested: Tf,
  servedApplied: string | null | undefined,
): boolean {
  if (servedApplied === undefined || servedApplied === null) return false;
  return resolveTf(requested, servedApplied).mismatch;
}

/**
 * Whether retained lane bars and freshly-served ones belong to ONE series (R55a).
 *
 * ⛔ THE HAZARD THIS EXISTS FOR ARRIVED WITH THE tf THREAD. `TfSwitcher`'s `onchange` only
 * ASSIGNS `tf`; nothing reloads. So the first poll after a switch returns bars at the NEW rung
 * while the lane still holds the OLD one, and the merge would interleave them — every bar
 * individually true, the series meaningless.
 * ⇒ ***BARS AT TWO RESOLUTIONS ARE NOT ONE SERIES.*** Compared on the SERVED rung, never the
 * requested one: the request is what we asked for, `tf_applied` is what we are holding.
 */
export function sameCvdRung(
  retainedApplied: string | null | undefined,
  freshApplied: string | null | undefined,
): boolean {
  return (retainedApplied ?? null) === (freshApplied ?? null);
}

/**
 * BOARD ① ② — the thinness threshold the default-window rule tests against.
 * ⚠ A CONSTANT, NOT A GUESS AT THE PANEL: 60 bars is one screen-worth at the rungs this chart
 * offers, so "thinner than a screen" is the condition the owner actually complained about. It
 * lives here beside MIN_SPAN_MS/MAX_SPAN_MS because it is viewport policy, not panel wiring —
 * the panel should not be the place a threshold gets invented.
 */
export const DEFAULT_WINDOW_MIN_BARS = 60;

/** Zoom floor: 5 minutes. Below this a 1m chart is a handful of slabs, not a chart. */
export const MIN_SPAN_MS = 5 * 60_000;

/**
 * THE SPAN CEILING AND FLOOR, IN BARS — because in MILLISECONDS they are a 1-minute chart's
 * numbers wearing a universal name (HUDCHAT 2026-08-20, R317).
 *
 * ⛔ WHAT THIS COST. `MAX_SPAN_MS = 4 days` reads as a generous zoom-out, and at 1m it is: 5,760
 * bars. One rung up it is the OWNER'S DEPTH LIMIT. Driven at my hand across the ladder:
 * ```
 *   1m   240 bars = 0.17d  fits    ✅        3h  240 bars =  30d  CLAMPED to  32 bars ⛔
 *   12m  240 bars = 2.00d  fits    ✅        1d  240 bars = 240d  CLAMPED to   4 BARS ⛔
 * ```
 * ⇒ ***THE RUNGS THAT "WORKED" WERE EXACTLY THE RUNGS WHOSE 240 BARS FIT INSIDE FOUR DAYS*** —
 * which is precisely the owner's report that only 1m and 12m worked, and the API was clean on all
 * five the whole time. A daily chart could show FOUR BARS however deep the payload was.
 *
 * ⇒ ⭐ ***A TIME-DENOMINATED CONSTANT ON A MULTI-RUNG CHART IS A BAR-DENOMINATED CONSTANT WITH ITS
 * RUNG LEFT IMPLICIT.*** Third instance of this one shape in a day — `PAGE_BARS` ("one UTC day per
 * page"), the `limit` 240, and this. Each correct where it was written; each meaningless one rung up.
 *
 * ⚠ THE BAR VALUES ARE CHOSEN SO 1m IS BYTE-IDENTICAL TO THE OLD CONSTANTS — 5 bars = 5 min,
 * 5,760 bars = 4 days. Nothing about the 1-minute chart changes; the ladder above it stops lying.
 */
export const MIN_SPAN_BARS = 5;
export const MAX_SPAN_BARS = 5760;

/**
 * Span clamps for a rung. `barMs` omitted ⇒ the legacy ms constants, so every caller that has not
 * been taught its rung keeps EXACTLY today's behaviour rather than silently changing shape.
 */
export function spanBounds(barMs?: number): { min: number; max: number } {
  if (barMs === undefined) return { min: MIN_SPAN_MS, max: MAX_SPAN_MS };
  if (!Number.isFinite(barMs) || barMs <= 0) {
    throw new Error(`spanBounds: barMs must be a positive finite ms span; got ${barMs}`);
  }
  return { min: MIN_SPAN_BARS * barMs, max: MAX_SPAN_BARS * barMs };
}
/** Zoom ceiling: 4 days — comfortably over a weekend gap, well under the whole spine. */
export const MAX_SPAN_MS = 4 * 24 * 60 * 60_000;
/** Follow mode's breathing room: the newest bar sits this fraction in from the right edge. */
export const FOLLOW_RIGHT_PAD_FRAC = 0.05;

/**
 * R2 (ORDER_HUD_CALIBRATION_1 round 2): THE LANDING WINDOW IS DERIVED FROM THE CHART'S OWN PLOT,
 * NEVER FROM THE SCREEN.
 *
 * ⛔ THE DEFECT THIS CURES, and it is the order's own P1 against itself: round 1 sized the window
 * from the viewport width, so a 2048 screen said 320 one-minute bars. Astra measured Chart A's
 * PLOT on that same load at about 710 px — a screen carries two charts and a rail — so the true
 * figure is 109 bars. Sizing from the screen over-asks by a factor of three and lands a window the
 * pane cannot draw.
 *
 * The bar widths are the order's DECLARED table, one place, quoted here so the panel cannot drift
 * from it. The gutter is R2's 8 pct of the plot, expressed in slots so the newest candle never
 * touches the price axis.
 *
 * ⚠ NOT IMPLEMENTED, AND DELIBERATELY NOT INVENTED: R2 also names "the minimum plot width below
 * which the timeframe's landing horizon halves". The order declares no numbers for it. R2's own
 * 8 pct was struck (R2538) for being an unmeasured desk number, so a second unmeasured table is
 * exactly the thing not to add — it is asked of the desk instead.
 *
 * R3090 (DESK-n78, 1036f30d box 0): 1m is 8 px, not R2's 6. The box asks for a 1m candle body of
 * at least 6 px at 2048; a 6 px SLOT draws a narrower body, so the slot is widened (47b876d5).
 */
export const LANDING_BAR_PX: Readonly<Record<string, number>> = Object.freeze({
  "1": 8, "3": 8, "6": 8, "12": 10, "24": 10, "48": 12, "180": 12, "360": 14, "1440": 14,
});

/** R2's right gutter, as a share of the plot. */
export const LANDING_GUTTER_FRAC = 0.08;

/**
 * R3090 (DESK-n78, 1036f30d box 0): the price pane's declared headroom, top and bottom, as a share
 * of the pane. lightweight-charts 5.2.1 defaults to top 0.2 and bottom 0.1, so before this the last
 * print could sit 10 pct from the pane's floor; the box asks at least 15 pct above AND below.
 */
export const PRICE_PANE_MARGIN_FRAC = 0.15;

/**
 * R2564(2), DESK-n74's ruling on R2's clause that declared no numbers: INSPECT is offered at
 * 288 px of plot — 48 slots at R2's 6 px, 4 of them gutter — and below it the pane is FOLLOW-only
 * under R4's phone layout. At R3090's 8 px the same 48 slots need 384 px at 1m.
 *
 * ⛔ THE FLOOR IS IN SLOTS, NOT PIXELS, and that is the part a pixel constant would get wrong.
 * 288 px buys 48 slots only at a 6 px bar; at 12m's 10 px the same plot buys 28, which is below
 * the floor the ruling actually names. Storing 288 as a pixel threshold would offer INSPECT on a
 * plot that cannot draw enough bars to inspect anything.
 */
export const INSPECT_FLOOR_SLOTS = 48;

/**
 * The pane draws a LOGICAL range, so the measured slots become {from,to} over the bar count —
 * the same shape focusLayout's `defaultFocusLogicalRange` returns, with every number derived from
 * the MEASURED plot instead of its fixed 180-and-6.
 *
 * ⚠ LEFT-ANCHORED WHEN THE DATA IS SHORT (R2): with fewer bars than the plot can hold, the window
 * starts at the first bar and never stretches. A negative `from` would scroll empty space in from
 * the left and read as missing history.
 */
export function landingLogicalRange(
  barCount: number,
  plotPx: number,
  tf: string,
  gutterFrac: number = LANDING_GUTTER_FRAC,
): { from: number; to: number } {
  if (!Number.isInteger(barCount) || barCount <= 0) {
    throw new Error(`landingLogicalRange: bar count must be a positive integer; got ${barCount}`);
  }
  const w = landingWindow(plotPx, tf, gutterFrac);
  return { from: Math.max(0, barCount - w.dataSlots), to: barCount - 1 + w.gutterSlots };
}

/** Whether the pane may offer INSPECT at this plot width and timeframe, per R2564(2). */
export function inspectOffered(plotPx: number, tf: string): boolean {
  return landingWindow(plotPx, tf).totalSlots >= INSPECT_FLOOR_SLOTS;
}

/**
 * R3 / A4: A RESIZE PRESERVES THE INTERVAL, NOT THE SPACING.
 *
 * A width change — orientation, fullscreen, the rail or a menu opening — must not move what the
 * eye is looking at. In INSPECT the user chose a time interval, so it survives a resize exactly
 * and the bars simply get wider or narrower to fit. In FOLLOW the anchor is the live edge and the
 * span is the thing preserved, so the same gutter share lands the right edge in the same place.
 *
 * ⛔ SPACING IS DERIVED, NEVER STORED. `barSpacingPx` computes it from plot over bars precisely so
 * that nothing can persist a spacing across a resize and silently rescale the window to keep it —
 * which is the shape of the defect R3 forbids.
 */
export function resizeViewport(
  vp: Viewport,
  liveEdgeTs: number,
  _newPlotPx: number,
  _tf: string,
  rightPadFrac: number = FOLLOW_RIGHT_PAD_FRAC,
): Viewport {
  if (!vp.follow) return { ...vp };
  return followViewport(liveEdgeTs, vp.toTs - vp.fromTs, undefined, rightPadFrac);
}

/** Pixels per bar for a window drawn on a plot — DERIVED, so a resize changes it by construction. */
export function barSpacingPx(vp: Viewport, plotPx: number, barMs: number): number {
  if (!Number.isFinite(plotPx) || plotPx <= 0) {
    throw new Error(`barSpacingPx: plot width must be positive and finite; got ${plotPx}`);
  }
  if (!Number.isFinite(barMs) || barMs <= 0) {
    throw new Error(`barSpacingPx: barMs must be positive and finite; got ${barMs}`);
  }
  return plotPx / ((vp.toTs - vp.fromTs) / barMs);
}


/**
 * R8 / A8 — RELOAD PERSISTENCE, gated by schema · instrument · timeframe.
 *
 * ⛔ THE FALL-THROUGH IS THE FEATURE. Restoring a window persisted against a different instrument
 * or an older schema puts the eye somewhere the data never was, and does it SILENTLY — the chart
 * looks deliberate. `null` means "use the landing window (R2)", which is always safe, so every
 * doubt resolves that way: a missing store, unparsable text, a half-written record, an unusable
 * interval. R8's words are that a missing or thrown store falls through to R2.
 *
 * ⚠ focusLayout's stored preferences carry NO version today, so every existing entry is a schema
 * MISS by construction and lands on R2. That is correct first-run behaviour, not data loss —
 * stating it because a silent "nothing restored" after a deploy reads like a bug otherwise.
 */
export const CHART_STATE_SCHEMA = 1;

export interface PersistedChartState {
  readonly schema: number;
  readonly instrument: string;
  readonly tf: string;
  readonly fromTs: number;
  readonly toTs: number;
  readonly follow: boolean;
}

export function restoredViewport(
  persisted: unknown,
  want: { instrument: string; tf: string },
): Viewport | null {
  let record: unknown = persisted;
  if (typeof record === "string") {
    try {
      record = JSON.parse(record);
    } catch {
      return null;
    }
  }
  if (record === null || typeof record !== "object") return null;
  const s = record as Partial<PersistedChartState>;
  if (s.schema !== CHART_STATE_SCHEMA) return null;
  if (s.instrument !== want.instrument || s.tf !== want.tf) return null;
  const { fromTs, toTs, follow } = s;
  if (!Number.isFinite(fromTs) || !Number.isFinite(toTs)) return null;
  if (!(Number(toTs) > Number(fromTs))) return null;
  if (typeof follow !== "boolean") return null;
  return { fromTs: Number(fromTs), toTs: Number(toTs), follow };
}

export interface LandingWindow {
  /** Whole bar slots the plot can hold at this timeframe's declared bar width. */
  totalSlots: number;
  /** Empty slots held ahead of price so the newest candle never touches the axis. */
  gutterSlots: number;
  /** Bars actually drawn: totalSlots - gutterSlots. */
  dataSlots: number;
}

/**
 * ⚠ `plotPx` is a DIVIDEND that must be real: a zero or NaN plot is the PRE-LAYOUT state, and
 * sizing from it silently lands a one-bar window that looks like a rendering bug rather than a
 * timing bug. It fails LOUD, in the same shape as `candleAskLimit` above.
 */
export function landingWindow(
  plotPx: number,
  tf: string,
  /** R2545/R2574: the caller PASSES its gutter, so the pane's 0.08 is readable where it is used. */
  gutterFrac: number = LANDING_GUTTER_FRAC,
): LandingWindow {
  if (!Number.isFinite(plotPx) || plotPx <= 0) {
    throw new Error(`landingWindow: plot width must be a positive finite px value; got ${plotPx}`);
  }
  const barPx = LANDING_BAR_PX[tf];
  if (barPx === undefined) {
    throw new Error(`landingWindow: no declared bar width for timeframe ${tf}`);
  }
  const totalSlots = Math.floor(plotPx / barPx);
  const gutterSlots = Math.round(gutterFrac * totalSlots);
  return { totalSlots, gutterSlots, dataSlots: totalSlots - gutterSlots };
}

/** Time → x. Linear in TIME: a gap minute owns exactly as many pixels as a traded one. */
export function timeToX(t: number, vp: Pick<Viewport, "fromTs" | "toTs">, width: number): number {
  return ((t - vp.fromTs) / (vp.toTs - vp.fromTs)) * width;
}

/** x → time. Exact inverse of timeToX. */
export function xToTime(x: number, vp: Pick<Viewport, "fromTs" | "toTs">, width: number): number {
  return vp.fromTs + (x / width) * (vp.toTs - vp.fromTs);
}

/** One bar's slot width in px under this viewport. */
export function barSlotPx(barMs: number, vp: Pick<Viewport, "fromTs" | "toTs">, width: number): number {
  return (barMs / (vp.toTs - vp.fromTs)) * width;
}

/**
 * Whether a window ending at `toTs` is still "at the live edge" — the predicate that
 * DERIVES follow after every interaction. One definition, used by zoom and pan both,
 * so the two cannot disagree about when the chart is live.
 */
export function coversLiveEdge(toTs: number, liveEdgeTs: number): boolean {
  return toTs >= liveEdgeTs;
}

/**
 * 5a26f4b5 box 1 - A HISTORY PREPEND KEEPS THE LOGICAL WINDOW, GUTTER INCLUDED.
 *
 * ⛔ THE DEFECT, traced in session 09-28 14:5xZ (HUDCHAT-39, gutter_trace39): the cockpit landed
 * with its 0.08 gutter (asked 371:458, shown 371:458 over newest index 451), then every eager
 * history page restored the pre-prepend TIME range - and a time range ends at the newest bar's time,
 * because the gutter's empty slots have no time. Shown fell to 520:600, 820:900, 1120:1200: the
 * gutter clipped to 0 on every load, in session, on both builds.
 * Prepending N older points shifts every existing index by N. Shifting the captured LOGICAL window
 * by how far one anchor bar's index moved restores the same bars AND the same empty slots ahead of
 * price. Pure: the caller reads both indices from the chart's own time scale (timeToIndex).
 */
export function shiftLogicalWindow(
  captured: { from: number; to: number; anchorIndex: number },
  anchorIndexNow: number,
): { from: number; to: number } {
  for (const [name, v] of [["from", captured.from], ["to", captured.to], ["anchorIndex", captured.anchorIndex], ["anchorIndexNow", anchorIndexNow]] as const) {
    if (!Number.isFinite(v)) throw new Error(`shiftLogicalWindow: ${name} must be finite; got ${v}`);
  }
  const shift = anchorIndexNow - captured.anchorIndex;
  return { from: captured.from + shift, to: captured.to + shift };
}

/**
 * Wheel zoom CENTERED ON THE CURSOR: the time under the pointer keeps its x-position
 * exactly, so the chart expands/contracts around the trader's point of attention —
 * never around the window center, which walks the anchor out from under the cursor.
 * `factor` > 1 zooms out, < 1 zooms in. Span clamps to [MIN_SPAN_MS, MAX_SPAN_MS].
 */
export function zoomAt(
  vp: Viewport,
  anchorTs: number,
  factor: number,
  liveEdgeTs: number,
  barMs?: number,
): Viewport {
  const span = vp.toTs - vp.fromTs;
  const bounds = spanBounds(barMs);
  const newSpan = Math.min(bounds.max, Math.max(bounds.min, span * factor));
  // The anchor's FRACTIONAL position is the invariant: same fraction of the new span.
  const frac = (anchorTs - vp.fromTs) / span;
  const fromTs = anchorTs - frac * newSpan;
  const toTs = fromTs + newSpan;
  return { fromTs, toTs, follow: coversLiveEdge(toTs, liveEdgeTs) };
}

/**
 * Drag-to-pan. `dxPx` is pointer movement since drag start (positive = dragged RIGHT),
 * which in trading-chart convention pulls OLDER bars into view: the window moves into
 * the past by the same time-distance the hand moved in screen-distance.
 */
export function panBy(vp: Viewport, dxPx: number, width: number, liveEdgeTs: number): Viewport {
  const span = vp.toTs - vp.fromTs;
  const dt = (-dxPx / width) * span;
  const fromTs = vp.fromTs + dt;
  const toTs = vp.toTs + dt;
  return { fromTs, toTs, follow: coversLiveEdge(toTs, liveEdgeTs) };
}

/**
 * The live-follow window: newest bar pinned FOLLOW_RIGHT_PAD_FRAC in from the right
 * edge, span preserved from whatever zoom the trader had. This is what double-click /
 * the LIVE button / every poll-in-follow-mode produce — the P0 behaviour as a mode.
 */
export function followViewport(
  liveEdgeTs: number,
  spanMs: number,
  barMs?: number,
  /**
   * R2545 (DESK-n74, 09-22), amending R2538: the pad is a PARAMETER defaulting to the landed
   * constant. The ten importers and their arms keep 0.05 untouched; SessionCvdPane passes R2's
   * 0.08, so the ordered gutter is met exactly where it was ordered and no panel that did not
   * ask for it moves. A bare re-pointing of the constant would have moved all ten.
   */
  rightPadFrac: number = FOLLOW_RIGHT_PAD_FRAC,
): Viewport {
  const bounds = spanBounds(barMs);
  const span = Math.min(bounds.max, Math.max(bounds.min, spanMs));
  const toTs = liveEdgeTs + span * rightPadFrac;
  return { fromTs: toTs - span, toTs, follow: true };
}

/**
 * Keep the window on the data, WITHOUT changing its span: overpan clamps by shifting.
 * The margins are half a span each side so the trader can put the oldest/newest bar
 * mid-screen, but cannot scroll the data entirely off it. When the data range is
 * narrower than the span, the right clamp wins — the live edge is the one a trading
 * chart must never lose.
 */
export function clampToData(vp: Viewport, dataStartTs: number, liveEdgeTs: number): Viewport {
  const span = vp.toTs - vp.fromTs;
  let { fromTs, toTs } = vp;
  const maxTo = liveEdgeTs + span / 2;
  const minFrom = dataStartTs - span / 2;
  if (toTs > maxTo) {
    fromTs -= toTs - maxTo;
    toTs = maxTo;
  }
  if (fromTs < minFrom) {
    const shift = Math.min(minFrom - fromTs, Math.max(0, maxTo - toTs));
    fromTs += shift;
    toTs += shift;
    if (fromTs < minFrom && toTs >= maxTo) {
      // Span exceeds the padded data range: pin the RIGHT edge, let the left overhang.
      toTs = maxTo;
      fromTs = toTs - span;
    }
  }
  return { fromTs, toTs, follow: vp.follow && coversLiveEdge(toTs, liveEdgeTs) };
}

/**
 * The index window [start, end) of bars this viewport draws. EDGE-INCLUSIVE: a bar
 * whose slot OVERLAPS the window is in, so candles slide off the frame instead of
 * blinking out at the boundary. `bars` must be oldest-first (the served contract).
 */
export function visibleSlice(
  barTs: number[],
  vp: Pick<Viewport, "fromTs" | "toTs">,
  barMs: number,
): { start: number; end: number } {
  let start = 0;
  while (start < barTs.length && barTs[start] + barMs <= vp.fromTs) start++;
  let end = barTs.length;
  while (end > start && barTs[end - 1] >= vp.toTs) end--;
  return { start, end };
}

// ─────────────────────────────────────────────────────────────────────────────────────
// TIME AXIS TICKS
// ─────────────────────────────────────────────────────────────────────────────────────

export interface TimeTick {
  t: number;
  /** "HH:MMZ", or "MM-DD HH:MMZ" on the first tick of a UTC day when the span is wide. */
  label: string;
}

/**
 * Candidate steps, minutes. Chosen so every step divides a day cleanly.
 *
 * ⛔ THE LADDER USED TO STOP AT 1440 — ONE DAY — AND THAT WAS THE AXIS HALF OF R317.
 * The step search takes the first candidate wide enough to clear `minPx`; when none does it
 * falls through to the LAST one. On a 730-day window nothing cleared 80px (one day is 1.4px
 * there), so it emitted **730 ticks** — a label per day, overlapping into an unreadable smear.
 * ⇒ ***THE DEPTH LAND MADE A TWO-YEAR WINDOW POSSIBLE AND THE AXIS COULD NOT DESCRIBE IT.***
 * The bar was on screen and the owner still could not tell it was 2024, which is the receipt.
 * Extended with day-multiples (2d · 1w · 2w · 30d · 90d · 180d · 365d) so the SAME search has
 * rungs to find. No call-site churn: `timeToX`/`xToTime`/`barSlotPx` are untouched — the
 * gap-compression rewrite across those remains its own object.
 */
const TICK_STEPS_MIN = [
  1, 2, 5, 10, 15, 30, 60, 120, 240, 360, 720, 1440,
  2880, 10_080, 20_160, 43_200, 129_600, 259_200, 525_600,
];

/** Beyond this the window straddles years and a bare `MM-DD` label is ambiguous. */
const YEAR_WIDE_MS = 180 * 24 * 60 * 60_000;

/**
 * Wall-clock-aligned ticks for the window. ⭐ THE POINT: ticks mark TIME, not data —
 * they land at their UTC-aligned instants whether or not a bar exists there, which is
 * what makes a gap VISIBLY a gap (labelled empty x-space, not elided space).
 */
export function timeTicks(
  vp: Pick<Viewport, "fromTs" | "toTs">,
  width: number,
  minPx = 80,
): TimeTick[] {
  const span = vp.toTs - vp.fromTs;
  if (!(span > 0) || !(width > 0)) return [];
  const stepMin =
    TICK_STEPS_MIN.find((m) => (m * 60_000 * width) / span >= minPx) ??
    TICK_STEPS_MIN[TICK_STEPS_MIN.length - 1];
  const stepMs = stepMin * 60_000;
  const dayWide = span > 24 * 60 * 60_000;
  // ⛔ A YEAR-WIDE WINDOW MUST SAY WHICH YEAR. `MM-DD HH:MMZ` cannot distinguish 2024-08-19
  // from 2026-08-19, and R286's receipt is literally "a 2024 bar ON SCREEN" — a date the reader
  // cannot resolve does not discharge it. Wide windows print `YYYY-MM-DD` and drop the clock,
  // which is noise at this scale.
  const yearWide = span > YEAR_WIDE_MS;
  const ticks: TimeTick[] = [];
  for (let t = Math.ceil(vp.fromTs / stepMs) * stepMs; t < vp.toTs; t += stepMs) {
    const d = new Date(t);
    const hm = d.toISOString().slice(11, 16) + "Z";
    const isMidnight = t % (24 * 60 * 60_000) === 0;
    const label = yearWide
      ? d.toISOString().slice(0, 10)
      : dayWide || isMidnight
        ? d.toISOString().slice(5, 10) + " " + hm
        : hm;
    ticks.push({ t, label });
  }
  return ticks;
}

// ─────────────────────────────────────────────────────────────────────────────────────
// BAR MERGE (scroll-back pages + live poll → one series)
// ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Merge two oldest-first bar arrays into one, DEDUPED BY `t` with `b` (the live poll)
 * winning a collision — the live feed is the authority on any bar both sources hold.
 * Output stays oldest-first. Never mutates its inputs.
 */
/**
 * The scroll-back retention bound (Codex P2 finding ①, driven by HUDCHAT-s 2026-08-15).
 *
 * ⛔ `mergeBars` had NO cap and every 1,440-bar page was retained for the life of the mount, while
 * mergeBars itself rebuilds a Map and re-sorts the WHOLE array on every page — so both memory and
 * per-page cost grow without bound as a trader pages backwards.
 * ⚠ HONEST BOUND ON THE SEVERITY: the spine is ~13.5k rows today (CC-aa's EXPLAIN), so the DATA
 * bounds this in practice and the code does not. It is real, it is not yet acute, and it grows.
 *
 * ⇒ THE CAP DROPS THE **OLDEST** BARS, NEVER THE NEWEST: the live edge is what the chart follows,
 * and evicting from the wrong end would silently amputate the present. 20,000 × 1m ≈ 14 days —
 * comfortably past the deepest scroll-back the pager can reach in one session.
 */
/**
 * The plot height for ONE chart instance (GK-aa's W4, `5f958c6d`).
 *
 * ⛔ WHY THIS IS A FUNCTION AND NOT AN INLINE `$derived`. My R3 arm asserted the price-tag geometry
 * used the IDENTIFIER `chartH` rather than the shared `CHART_H` the owner's P1.5 dual-pane bind
 * killed. GK-aa beat it in one edit: `chartH = $derived(BASE_CHART_H)` KEEPS the identifier and
 * COLLAPSES the definition — green on all 14 suites, while the expanded pane silently renders at
 * the base height.
 * ⇒ ***AN ARM ON A NAME IS NOT AN ARM ON A VALUE.*** Extracting the derivation makes the VALUE
 * drivable, which is the same cure this module already applies to `deepenHistory`: a property you
 * can only assert about is a property you cannot test.
 */
export function plotHeight(big: boolean, bigH: number, baseH: number): number {
  return big ? bigH : baseH;
}

export const MAX_HISTORY_BARS = 20_000;

/** Bound a merged history to the newest MAX_HISTORY_BARS. Pure, so it can be armed directly. */
export function capHistory<T extends { t: number }>(bars: T[], max = MAX_HISTORY_BARS): T[] {
  return bars.length <= max ? bars : bars.slice(bars.length - max);
}

/**
 * The deep-fetch fallback as a PURE state transition (Codex P2 ②; GK-aa's bar `0da47121` ②).
 *
 * ⛔ IT LIVES HERE RATHER THAN INSIDE THE COMPONENT SO THE ARM CAN **DRIVE THE THROW PATH**, not
 * merely read the source order. GK-aa's clause: *"the arm drives the transient-throw path and
 * asserts bars>0 AND the 'history ended' string ABSENT"* — a text assertion about statement order
 * cannot do either, and a source-order arm passes on any refactor that preserves the ordering while
 * breaking the behaviour.
 *
 * ⛔⛔ THE LATCH IS SET ONLY AFTER `fetchDeep` RESOLVES. The shipped form set it first, so ONE
 * transient throw permanently exhausted scroll-back: the retry short-circuited with ZERO bars and
 * the pane stated "history treated as ended there".
 * ⇒ ***A NETWORK FAULT MUST NEVER RENDER AS A FACT ABOUT THE MARKET.*** A failed attempt leaves the
 * door open; only a SUCCEEDED deepen may close it. Throws propagate — the caller states the fault.
 */
export interface DeepenState {
  deepened: boolean;
  exhausted: boolean;
}

export async function deepenHistory<T extends { t: number }>(
  state: DeepenState,
  history: T[],
  fetchDeep: () => Promise<T[]>,
  max = MAX_HISTORY_BARS,
): Promise<{ state: DeepenState; history: T[] }> {
  if (state.deepened) {
    return { state: { deepened: true, exhausted: true }, history };
  }
  const bars = await fetchDeep();
  return {
    state: { deepened: true, exhausted: true },
    history: capHistory(mergeBars(history, bars), max),
  };
}

export function mergeBars<T extends { t: number }>(a: T[], b: T[]): T[] {
  const byT = new Map<number, T>();
  for (const bar of a) byT.set(bar.t, bar);
  for (const bar of b) byT.set(bar.t, bar);
  return [...byT.values()].sort((x, y) => x.t - y.t);
}

/** Plain wheel owns time; either supported modifier hands the gesture to price. */
export function wheelIntent(mods: { shiftKey: boolean; altKey: boolean }): "time" | "price" {
  return mods.shiftKey || mods.altKey ? "price" : "time";
}

/* ==============================================================================================
 * THE PRICE DOMAIN — y-zoom. Spec: docs/specs/HUD_Y_ZOOM_PRICE_DOMAIN.md (owner ask `2928fc2a` ②).
 *
 * ⛔ WHY THIS LIVES HERE AND NOT IN A `priceViewport.ts`. The time half above already fixes the
 * law — the anchor's FRACTIONAL position is the invariant, the span is clamped at both ends, and
 * `follow` says whether the window is tracking the data or the user. **Price obeys the same law on
 * a second axis.** A separate module would duplicate that arithmetic, and the copy drifts from the
 * original the first time either is fixed.
 * ⇒ ***`auto` IS THE PRICE ANALOGUE OF `follow`.*** Any manual gesture clears it, exactly as a drag
 * clears follow — and clearing it is what the "Y MANUAL" surface state renders from.
 * ============================================================================================== */

export interface PriceDomain {
  /** Bottom / top of the visible price window. */
  loP: number;
  hiP: number;
  /** TRUE = the engine's autoscale owns the domain. The default, and the only pre-y-zoom behaviour. */
  auto: boolean;
}

/**
 * Zoom floor: 1 point. ES trades in quarter points, and `axisTicks` already switches to 2dp under a
 * 40-point span — below a point the axis is drawing rounding noise, not price.
 */
export const MIN_PRICE_SPAN = 1;

/**
 * ⛔ A PASS-THROUGH ON PURPOSE — IT MUST ADD NOTHING. Callers hand in the range they already
 * intend to draw, and this only stamps `auto:true` on it. R6 pins that: invent padding here and
 * every chart that never touches y-zoom changes silently.
 *
 * ⚠ CORRECTED 2026-08-16 — MY OWN COMMENT HERE WAS FALSE. It read *"the engine's autoscale …
 * INCLUDING ITS LACK OF PADDING"* and claimed `renderData.priceRange` carries no pad. **It does:**
 * `EsChartPanel.autoPriceRange` applies `(max - min) * 0.04 || 1` to both ends so wicks do not clip.
 * The DESIGN was right — padding belongs to the panel, this stays a pass-through — but the stated
 * REASON was a fact I never measured, and it nearly cost the pad: reviewing the diff, I read the
 * relocation of those lines as a deletion and almost filed a regression that did not exist.
 * ⇒ ***A FALSE COMMENT SURVIVES ITS AUTHOR AND IS READ AS A MEASUREMENT.*** (Same class as the
 * `TOUCHING` note this repo retired: a warning that outlives the defect it warns about.)
 */
export function autoPriceDomain(dataLo: number, dataHi: number): PriceDomain {
  return { loP: dataLo, hiP: dataHi, auto: true };
}

/** Double-click / reset: hand the axis back to the engine. One act, not a second control. */
export function resetPriceDomain(dataLo: number, dataHi: number): PriceDomain {
  return autoPriceDomain(dataLo, dataHi);
}

/**
 * The price under `anchorP` does not move. Same invariant as `zoomAt`, same shape, deliberately.
 * Always returns `auto:false` — reaching this function IS the manual gesture.
 */
export function priceZoomAt(
  pd: PriceDomain,
  anchorP: number,
  factor: number,
  dataLo: number,
  dataHi: number,
): PriceDomain {
  const span = pd.hiP - pd.loP;
  const maxSpan = Math.max(MIN_PRICE_SPAN, dataHi - dataLo);
  const newSpan = Math.min(maxSpan, Math.max(MIN_PRICE_SPAN, span * factor));
  // The anchor's FRACTIONAL position is the invariant — the same law the time axis obeys.
  const frac = (anchorP - pd.loP) / span;
  const loP = anchorP - frac * newSpan;
  return clampPriceToData({ loP, hiP: loP + newSpan, auto: false }, dataLo, dataHi);
}

/**
 * Keep the window inside the drawn data. ⚠ CLAMPING TRANSLATES, IT NEVER SQUASHES — a clamp that
 * shrinks the span would silently change the zoom level the user chose, which is the same class of
 * defect as a scale that lies about itself.
 */
export function clampPriceToData(pd: PriceDomain, dataLo: number, dataHi: number): PriceDomain {
  const span = pd.hiP - pd.loP;
  let { loP, hiP } = pd;
  if (hiP > dataHi) {
    const d = hiP - dataHi;
    hiP -= d;
    loP -= d;
  }
  if (loP < dataLo) {
    const d = dataLo - loP;
    loP += d;
    hiP += d;
    // Span wider than the data: pin the TOP and let the bottom overhang — the price mirror of the
    // time half pinning the RIGHT edge.
    if (hiP > dataHi) {
      hiP = dataHi;
      loP = hiP - span;
    }
  }
  return { loP, hiP, auto: pd.auto };
}

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// BOARD ① — THE COMPRESSED AXIS (R47 ①, spec R49). The mapping stops being linear here.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

/** A segment with the pixel budget the axis actually gives it. */
export interface DrawnSegment {
  from: number;
  to: number;
  closed: boolean;
  /** px allocated to this span. Closed spans get `CLOSED_SPAN_PX`, open spans their real share. */
  px: number;
  /** px offset of this segment's left edge. */
  x0: number;
}

/**
 * A closed span draws as a fixed, narrow, MARKED band — never zero.
 * ⛔ NEVER ZERO ON PURPOSE: a zero-width closed span would make the axis CONTINUOUS across a
 * weekend, which is the interpolation lie the whole lane refuses elsewhere (`alignCvdLane` NULLs
 * a missing minute rather than bridging it). R49's word is "compress, MARKED" — the axis must
 * still say a gap happened. 12px is visible at every viewport this panel supports.
 */
export const CLOSED_SPAN_PX = 12;

/**
 * Allocate pixels across calendar segments: closed spans get a fixed band, open spans share the
 * remainder IN PROPORTION TO THEIR REAL DURATION (so an open hour still reads as an hour).
 *
 * ⚠ THE DEGENERATE CASE IS NAMED, NOT ASSUMED AWAY: if the closed bands alone exceed `width`
 * (a 4-day window on a narrow pane), open time would get negative pixels. Bands are then scaled
 * DOWN proportionally so open time keeps a floor — a chart that cannot show its trading hours has
 * compressed the wrong thing.
 */
export function layoutSegments(segments: DrawnSegment[] | SegmentInput[], width: number): DrawnSegment[] {
  if (!Number.isFinite(width) || width < 0) {
    throw new Error(`layoutSegments: width must be a positive finite px; got ${width}`);
  }
  if (width === 0) return [];
  const segs = segments as SegmentInput[];
  if (segs.length === 0) throw new Error("layoutSegments: no segments — a window always has one");
  const openMs = segs.reduce((a, s) => a + (s.closed ? 0 : s.to - s.from), 0);
  const closedCount = segs.filter((s) => s.closed).length;
  let bandPx = CLOSED_SPAN_PX;
  const OPEN_FLOOR_FRAC = 0.5;
  if (closedCount * bandPx > width * (1 - OPEN_FLOOR_FRAC)) {
    bandPx = (width * (1 - OPEN_FLOOR_FRAC)) / closedCount;
  }
  const openPx = width - closedCount * bandPx;
  const out: DrawnSegment[] = [];
  let x = 0;
  for (const s of segs) {
    const px = s.closed
      ? bandPx
      : openMs === 0 ? 0 : ((s.to - s.from) / openMs) * openPx;
    out.push({ from: s.from, to: s.to, closed: s.closed, px, x0: x });
    x += px;
  }
  return out;
}

export interface SegmentInput { from: number; to: number; closed: boolean }

/**
 * time → x across a PIECEWISE axis.
 * ⛔ `timeToX` above is the LINEAR map and stays exactly as it was — the uncompressed panes still
 * use it. This is a second, explicitly non-linear map, not a replacement, so nothing that never
 * asked for compression silently gets it.
 */
export function compressedTimeToX(t: number, drawn: DrawnSegment[]): number {
  if (drawn.length === 0) throw new Error("compressedTimeToX: no drawn segments");
  const first = drawn[0];
  const last = drawn[drawn.length - 1];
  if (t <= first.from) return first.x0;
  if (t >= last.to) return last.x0 + last.px;
  for (const s of drawn) {
    if (t >= s.from && t < s.to) {
      const span = s.to - s.from;
      return s.x0 + (span === 0 ? 0 : ((t - s.from) / span) * s.px);
    }
  }
  return last.x0 + last.px;
}

/**
 * x → time. EXACT INVERSE of `compressedTimeToX`, and that is a TESTED property now, not a
 * doc-comment. ⇒ ***THE INVERSE MUST BE AN ARM THE MOMENT THE MAP STOPS BEING LINEAR*** — the
 * linear pair could assert it in prose because it was one division; a piecewise pair cannot.
 * ⚠ INSIDE A COMPRESSED BAND THE INVERSE IS LOSSY BY CONSTRUCTION (49 hours share 12px), so the
 * round trip is exact in PIXELS, never in milliseconds. Stated because a caller who expects
 * ms-exactness inside a weekend band would be silently wrong.
 */
export function compressedXToTime(x: number, drawn: DrawnSegment[]): number {
  if (drawn.length === 0) throw new Error("compressedXToTime: no drawn segments");
  const first = drawn[0];
  const last = drawn[drawn.length - 1];
  if (x <= first.x0) return first.from;
  const end = last.x0 + last.px;
  if (x >= end) return last.to;
  for (const s of drawn) {
    if (x >= s.x0 && x < s.x0 + s.px) {
      return s.px === 0 ? s.from : s.from + ((x - s.x0) / s.px) * (s.to - s.from);
    }
  }
  return last.to;
}

/**
 * One bar's drawn width — the signature that COULD NOT express the answer before.
 * ⛔ `barSlotPx(barMs, vp, width)` derives width from DURATION ALONE. Under a piecewise axis a
 * bar's width depends on WHERE IT SITS: the same 12 minutes is ~1px inside a weekend band and its
 * full share inside an open span. ⇒ ***A SIGNATURE WITHOUT POSITION CANNOT EXPRESS A CORRECT
 * ANSWER*** — the in-lane decision I published for grading before building this.
 */
export function compressedBarSlotPx(barStartTs: number, barMs: number, drawn: DrawnSegment[]): number {
  return compressedTimeToX(barStartTs + barMs, drawn) - compressedTimeToX(barStartTs, drawn);
}

/**
 * BOARD ① ② — THE DEFAULT-WINDOW RULE (R47 ②).
 *
 * THE OWNER'S COMPLAINT THIS EXISTS TO ANSWER: on a Sunday-evening load the live session is
 * minutes old, `followViewport` gives it a window sized in bars, and ~49 closed hours sit to the
 * left — so the chart opens on a handful of fresh bars with history shoved off-screen. He called
 * it "the chart disappeared". It had not; it was showing him a correct window of an empty span.
 *
 * ⇒ WHEN THE LIVE SESSION IS THINNER THAN `minBars`, REACH BACK PAST THE CLOSED SPAN INTO THE
 * PRIOR SESSION, so Friday's tape opens beside Sunday's.
 *
 * ⚠ SEGMENTS ARE INJECTED, NOT DERIVED HERE. `chartViewport` holds no calendar and must not grow
 * one: `sessionAt` is the single authority and `sessionSegments` is its walker. Passing them in
 * also makes this drivable from fixtures without a clock — the `feedState` lesson, where an
 * un-injected `now` silently used the real one and manufactured a regression that did not exist.
 *
 * ⛔ IT WIDENS, IT NEVER NARROWS. If the live session is already fat the window is returned
 * UNTOUCHED — a rule that also shrank would fight the trader's own zoom on a busy afternoon, and
 * the complaint was never "too much history".
 */
export function initialViewport(
  liveEdgeTs: number,
  spanMs: number,
  barMs: number,
  minBars: number,
  segments: SegmentInput[],
): Viewport {
  if (!Number.isFinite(barMs) || barMs <= 0) {
    throw new Error(`initialViewport: barMs must be a positive finite ms; got ${barMs}`);
  }
  // ⚠ THE BASE IS RUNG-AWARE TOO. This function already knew its rung; the window it starts from
  // did not, so a daily first window was clamped to four days before the rule ever ran.
  const base = followViewport(liveEdgeTs, spanMs, barMs);
  if (segments.length === 0) return base;

  // The CURRENT open span is the last non-closed segment covering the live edge.
  const openNow = [...segments].reverse().find((s) => !s.closed && s.from <= liveEdgeTs);
  if (openNow === undefined) return base;

  const liveBars = (liveEdgeTs - openNow.from) / barMs;
  if (liveBars >= minBars) return base;      // already fat — widen nothing

  // Reach back THROUGH the closed span to the prior open segment's start, so the window
  // contains real bars on both sides of the gap rather than the gap plus a sliver.
  const idx = segments.indexOf(openNow);
  const priorOpen = segments.slice(0, idx).reverse().find((s) => !s.closed);
  // ⚠ NO PRIOR SESSION IN THE WINDOW ⇒ RETURN BASE, never a fabricated reach. The honest answer
  // to "there is no history here" is the window we already had, not one invented to look full.
  if (priorOpen === undefined) return base;

  const fromTs = Math.min(base.fromTs, priorOpen.from);
  // `follow` stays TRUE: this is still a live chart pinned to the edge, just a wider one.
  // Flipping it false would silently disable auto-advance on exactly the load where the owner
  // is waiting for the first fresh bar to appear.
  return { fromTs, toTs: base.toTs, follow: true };
}
